import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, PlayCircle, PenLine, Lock } from "lucide-react";
import { SiteHeader } from "@/components/SiteChrome";
import { CoachPanel } from "@/components/CoachPanel";
import { InterviewHistory } from "@/components/InterviewHistory";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/lib/auth";
import { api } from "@/lib/api";

export function ModuleList({ modules, current, nextSlug, compact = false }) {
  return (
    <ol className="space-y-1.5" data-testid="module-nav-list">
      {modules.map((m) => {
        const active = m.slug === current;
        return (
          <li key={m.slug}>
            <Link to={`/course/${m.slug}`} className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors ${active ? "bg-teal-50 border border-teal-200" : "hover:bg-slate-100 border border-transparent"}`} data-testid={`module-nav-${m.number}`} aria-current={active ? "page" : undefined}>
              {m.completed ? <CheckCircle2 className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" aria-label="Completed" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0 mt-0.5" aria-label="Not completed" />}
              <span className="min-w-0">
                <span className={`block text-sm ${active ? "font-semibold text-teal-900" : "text-slate-700"}`}>{m.number}. {m.title}</span>
                {!compact && m.slug === nextSlug && !m.completed && <span className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Recommended next</span>}
              </span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}

function Countdown({ to }) {
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((x) => x + 1), 60000); return () => clearInterval(id); }, []);
  const ms = Math.max(0, new Date(to) - Date.now());
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return <span className="tabular-nums" data-testid="unlock-countdown">{d}d {h}h {m}m</span>;
}

function LockedDashboard({ d }) {
  const when = new Date(d.user.access_unlocks_at);
  const days = { "3day": "3 days", "1week": "1 week", "2week": "2 weeks" }[d.user.tier];
  return (
    <div className="min-h-screen pb-24">
      <SiteHeader links={false} />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-10 space-y-8">
        <section className="rounded-3xl bg-gradient-to-br from-teal-800 to-sky-900 text-white p-7 sm:p-10 relative overflow-hidden grain rise" data-testid="course-locked-banner">
          <Lock className="h-8 w-8 text-teal-100" aria-hidden />
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4">Your course will open {days ? `after ${days}` : "soon"}</h1>
          <p className="text-teal-50/90 mt-3 text-base sm:text-lg">Opens on <strong data-testid="unlock-date">{when.toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</strong> · <Countdown to={d.user.access_unlocks_at} /> to go</p>
          <p className="text-teal-50/80 mt-4 text-sm max-w-2xl">Your payment is confirmed and your account is ready. We'll email you the moment your course opens. Then sign in with the email and password you created, and every module, video and download will be available.</p>
          {!d.user.has_password && <Button asChild className="mt-6 rounded-full bg-white text-teal-900 hover:bg-teal-50" data-testid="locked-create-account"><Link to="/account/setup">Finish creating your account</Link></Button>}
        </section>
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900">What's waiting for you</h2>
          <ol className="grid md:grid-cols-2 gap-3">
            {d.modules.map((m) => (
              <li key={m.slug} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-4 opacity-80" data-testid={`locked-module-${m.number}`}>
                <Lock className="h-4 w-4 text-slate-400 shrink-0" aria-label="Locked" />
                <span className="text-sm text-slate-700">{m.number}. {m.title}</span>
              </li>
            ))}
          </ol>
        </section>
      </main>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/course/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <><SiteHeader links={false} /><PageLoader /></>;
  if (d.user.locked) return <LockedDashboard d={d} />;
  const resume = d.modules.find((m) => m.slug === d.last_module_slug) || d.modules.find((m) => m.slug === d.next_module_slug) || d.modules[0];
  return (
    <div className="min-h-screen pb-24">
      <SiteHeader links={false} />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-10">
        <section className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
          <div className="rounded-3xl bg-gradient-to-br from-teal-800 to-sky-900 text-white p-7 sm:p-10 relative overflow-hidden grain rise" data-testid="resume-card">
            <p className="text-xs uppercase tracking-[0.2em] text-teal-100">{d.completed ? "Resume where you left off" : "Start here"}</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">Module {resume.number}: {resume.title}</h1>
            <p className="text-teal-50/90 mt-3 max-w-xl text-sm sm:text-base">{resume.summary}</p>
            <Button asChild size="lg" className="mt-6 rounded-full bg-white text-teal-900 hover:bg-teal-50" data-testid="resume-module-button">
              <Link to={`/course/${resume.slug}`}><PlayCircle className="h-5 w-5 mr-2" />{d.completed ? "Continue" : "Begin Module 1"}</Link>
            </Button>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-7 space-y-5 rise" style={{ animationDelay: "100ms" }}>
            <h2 className="text-lg font-bold text-slate-900">Your progress</h2>
            <div className="flex items-end gap-3"><span className="font-display text-5xl font-extrabold text-teal-700 tabular-nums" data-testid="progress-percent">{d.percent}%</span><span className="text-sm text-slate-500 pb-2">{d.completed} of {d.total} modules complete</span></div>
            <Progress value={d.percent} className="h-2.5" aria-label="Course progress" data-testid="progress-bar" />
            <p className="text-sm text-slate-600">Modules are listed in the recommended order, but you can open any module at any time.</p>
          </div>
        </section>
        <section className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 space-y-4" data-testid="dashboard-interview-scores">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Mock interview scores</h2>
            <p className="text-sm text-slate-500">Each answer is scored out of 10 using STAR plus role fit</p>
          </div>
          <InterviewHistory />
        </section>
        <section className="space-y-4">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">All modules</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {d.modules.map((m) => (
              <Link key={m.slug} to={`/course/${m.slug}`} className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-teal-300 hover:shadow-md transition-[border-color,box-shadow] flex flex-col gap-3" data-testid={`dashboard-module-${m.number}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display text-2xl font-extrabold text-teal-600/80 tabular-nums">{String(m.number).padStart(2, "0")}</span>
                  {m.completed ? <span className="text-xs font-semibold text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-0.5">Completed</span>
                    : m.slug === d.next_module_slug ? <span className="text-xs font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-0.5">Recommended next</span> : null}
                </div>
                <h3 className="font-bold text-slate-900 leading-snug">{m.title}</h3>
                <p className="text-sm text-slate-600 line-clamp-2">{m.summary}</p>
                <div className="mt-auto flex items-center justify-between text-xs text-slate-500 pt-2">
                  <span className="flex items-center gap-1">{m.has_answer && <><PenLine className="h-3.5 w-3.5" /> Exercise saved</>}</span>
                  <ArrowRight className="h-4 w-4 text-teal-700 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <CoachPanel />
    </div>
  );
}
